import { useEffect, useState } from "react";
import { useSaveSetting, useSettings, type Setting } from "../../api/admin";
import { formatDateTime } from "../../api/dates";
import AdminLayout from "./AdminLayout";

const KIND_HINT: Record<Setting["kind"], string> = {
  fraction: "15% or 0.15",
  int: "a whole number",
  text: "text",
  flag: "on or off",
  decimal: "an amount, such as 1500.00",
};

/** The commercial settings: one card per setting, each change with its
 * reason. A setting is effective-dated, so a change never restates a
 * document already issued at the old value; the history under each card is
 * that record. */
export function SettingsPage() {
  const view = useSettings();
  return (
    <AdminLayout title="Settings" sub="Values printed on documents and used in pricing">
      {view.isError ? <p className="notice bad">The settings could not be loaded: {view.error.message}</p> : null}
      {view.isPending ? <div className="table-card empty-state">Loading settings…</div> : null}
      {view.data?.saved ? <p className="notice ok">Saved {view.data.saved}.</p> : null}
      {view.data ? (
        <div className="settings-grid">
          {view.data.rows.map((row) => (
            <SettingCard key={row.name} row={row} />
          ))}
        </div>
      ) : null}
    </AdminLayout>
  );
}

/** One setting, its reason-gated form and its history. Exported so the Labour
 * allowances screen can show the default hours and the minimum charge beside
 * the table they belong with. */
export function SettingCard({ row }: { row: Setting }) {
  const save = useSaveSetting();
  const [value, setValue] = useState(row.value);
  const [reason, setReason] = useState("");
  useEffect(() => setValue(row.value), [row.value]);
  const headingId = `setting-${row.name}`;
  return (
    <section className="card setting-card" aria-labelledby={headingId}>
      <div className="setting-head">
        <h3 id={headingId}>{row.label}</h3>
        <span className={row.kind === "text" ? "setting-value mono" : "setting-value setting-value-figure mono"}>
          {row.value}
        </span>
      </div>
      <p className="muted-note">{row.why}</p>
      <form
        className="setting-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({ key: row.name, value: value.trim(), reason: reason.trim() }, { onSuccess: () => setReason("") });
        }}
      >
        {row.kind === "flag" ? (
          <label className="field">
            Value ({KIND_HINT[row.kind]}; default {row.default})
            <select value={value} onChange={(e) => setValue(e.target.value)}>
              <option value="on">On</option>
              <option value="off">Off</option>
            </select>
          </label>
        ) : (
          <label className="field">
            Value ({KIND_HINT[row.kind]}; default {row.default})
            <input value={value} onChange={(e) => setValue(e.target.value)} />
          </label>
        )}
        <label className="field">
          Why it is changing
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Required" />
        </label>
        <button type="submit" className="btn btn-primary" disabled={save.isPending || reason.trim() === ""}>
          Save
        </button>
      </form>
      {save.isError ? <p className="notice bad">{save.error.message}</p> : null}
      {row.history.length > 0 ? (
        <details className="setting-history">
          <summary>History ({row.history.length})</summary>
          <table className="data-table">
            <thead>
              <tr>
                <th>Value</th>
                <th>From</th>
                <th>By</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {row.history.map((h, i) => (
                <tr key={i}>
                  <td className="mono">{h.value}</td>
                  <td>{formatDateTime(h.effective_from)}</td>
                  <td>{h.by ?? "—"}</td>
                  <td>{h.reason ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ) : (
        <p className="muted-note">Never configured; the default is in force.</p>
      )}
    </section>
  );
}
